import { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { Calendar } from '@/components/ui/calendar';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  CalendarIcon,
  Clock,
  Target,
  Brain,
  CheckCircle,
  AlertCircle,
  Sparkles,
} from 'lucide-react';
import { format } from 'date-fns';
import { useUser } from '@/contexts/UserContext';
import { goalsService } from '@/services/goalsService';
import { notificationsService } from '@/services/notificationsService';
import { toast } from 'sonner';

export interface ActionableInsight {
  id: string;
  title: string;
  description: string;
  impact: 'high' | 'medium' | 'low';
  category: 'productivity' | 'wellbeing' | 'growth' | 'collaboration';
  actionType: 'goal' | 'schedule' | 'habit' | 'connect' | 'resource';
  suggestedAction: {
    type: string;
    details: any;
  };
  reasoning: string;
  expectedOutcome: string;
}

interface ActionModalProps {
  isOpen: boolean;
  onClose: () => void;
  insight: ActionableInsight | null;
  onActionComplete?: () => void;
}

export const ActionModal = ({ isOpen, onClose, insight, onActionComplete }: ActionModalProps) => {
  const { user } = useUser();
  const [isExecuting, setIsExecuting] = useState(false);
  const [customization, setCustomization] = useState<any>({});

  if (!insight) return null;

  const checkForConflicts = async (actionData: any) => {
    // Check for duplicate goals
    if (insight.actionType === 'goal') {
      const response = await goalsService.getGoals();
      const existingGoals = response.data || [];
      const similarGoal = existingGoals.find((g: any) => 
        g.status === 'active' && 
        g.title?.toLowerCase().includes(actionData.title?.toLowerCase() || '')
      );

      if (similarGoal) {
        throw new Error(`You already have a similar goal: "${similarGoal.title}". Consider updating it instead.`);
      }
    }

    // Check for duplicate habits - TODO: Implement when habits service is available
    if (insight.actionType === 'habit') {
      // Habits are not yet implemented in the new backend
      // This check can be removed or implemented when habits service is available
    }

    // Check for scheduling conflicts
    if (insight.actionType === 'schedule') {
      const response = await notificationsService.getNotifications({ 
        type: 'insight',
        limit: 100 
      });
      const existingSchedules = response.data || [];
      const today = new Date().toISOString().split('T')[0];
      const todaySchedules = existingSchedules.filter((s: any) => 
        s.createdAt?.startsWith(today) && 
        s.data?.action_type === 'scheduled_activity'
      );

      const conflictingTime = todaySchedules.find((schedule: any) => 
        schedule.data?.time === actionData.time
      );
      
      if (conflictingTime) {
        throw new Error(`You already have an activity scheduled for ${actionData.time}. Choose a different time.`);
      }
    }
  };

  const getActionUI = () => {
    switch (insight.actionType) {
      case 'goal':
        return (
          <div className="space-y-4">
            <div>
              <Label htmlFor="goal-title">Goal Title</Label>
              <Input
                id="goal-title"
                defaultValue={insight.suggestedAction.details.title}
                onChange={(e) => setCustomization({ ...customization, title: e.target.value })}
              />
            </div>
            <div>
              <Label htmlFor="goal-description">Description</Label>
              <Textarea
                id="goal-description"
                defaultValue={insight.suggestedAction.details.description}
                onChange={(e) => setCustomization({ ...customization, description: e.target.value })}
                rows={3}
              />
            </div>
            <div>
              <Label htmlFor="goal-date">Target Date</Label>
              <Calendar
                mode="single"
                selected={customization.targetDate || new Date()}
                onSelect={(date) => setCustomization({ ...customization, targetDate: date })}
                className="rounded-md border"
              />
            </div>
          </div>
        );

      case 'schedule':
        return (
          <div className="space-y-4">
            <div>
              <Label htmlFor="activity">Activity</Label>
              <Input
                id="activity"
                defaultValue={insight.suggestedAction.details.activity}
                onChange={(e) => setCustomization({ ...customization, activity: e.target.value })}
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="time">Time</Label>
                <Select
                  defaultValue={insight.suggestedAction.details.time}
                  onValueChange={(value) => setCustomization({ ...customization, time: value })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="morning">Morning (9-12)</SelectItem>
                    <SelectItem value="afternoon">Afternoon (12-5)</SelectItem>
                    <SelectItem value="evening">Evening (5-8)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label htmlFor="duration">Duration</Label>
                <Select
                  defaultValue={insight.suggestedAction.details.duration}
                  onValueChange={(value) => setCustomization({ ...customization, duration: value })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="15">15 minutes</SelectItem>
                    <SelectItem value="30">30 minutes</SelectItem>
                    <SelectItem value="45">45 minutes</SelectItem>
                    <SelectItem value="60">1 hour</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label htmlFor="frequency">Frequency</Label>
              <Select
                defaultValue={insight.suggestedAction.details.frequency}
                onValueChange={(value) => setCustomization({ ...customization, frequency: value })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="daily">Daily</SelectItem>
                  <SelectItem value="weekly">Weekly</SelectItem>
                  <SelectItem value="biweekly">Bi-weekly</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        );

      case 'habit':
        return (
          <div className="space-y-4">
            <div>
              <Label htmlFor="habit">Habit to Build</Label>
              <Input
                id="habit"
                defaultValue={insight.suggestedAction.details.habit}
                onChange={(e) => setCustomization({ ...customization, habit: e.target.value })}
              />
            </div>
            <div>
              <Label htmlFor="trigger">Trigger/Reminder</Label>
              <Input
                id="trigger"
                placeholder="e.g., After morning coffee"
                defaultValue={insight.suggestedAction.details.trigger}
                onChange={(e) => setCustomization({ ...customization, trigger: e.target.value })}
              />
            </div>
            <div>
              <Label htmlFor="tracking">How to Track Progress</Label>
              <Select
                defaultValue={insight.suggestedAction.details.tracking || 'checkin'}
                onValueChange={(value) => setCustomization({ ...customization, tracking: value })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="checkin">Daily Check-in</SelectItem>
                  <SelectItem value="journal">Reflection Journal</SelectItem>
                  <SelectItem value="counter">Simple Counter</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        );

      case 'connect':
        return (
          <div className="space-y-4">
            <div>
              <Label htmlFor="person">Who to Connect With</Label>
              <Input
                id="person"
                placeholder="Team member or mentor name"
                defaultValue={insight.suggestedAction.details.person}
                onChange={(e) => setCustomization({ ...customization, person: e.target.value })}
              />
            </div>
            <div>
              <Label htmlFor="purpose">Purpose of Connection</Label>
              <Textarea
                id="purpose"
                defaultValue={insight.suggestedAction.details.purpose}
                onChange={(e) => setCustomization({ ...customization, purpose: e.target.value })}
                rows={2}
              />
            </div>
            <div>
              <Label htmlFor="method">How to Connect</Label>
              <Select
                defaultValue={insight.suggestedAction.details.method || 'chat'}
                onValueChange={(value) => setCustomization({ ...customization, method: value })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="chat">In-app Chat</SelectItem>
                  <SelectItem value="meeting">Schedule Meeting</SelectItem>
                  <SelectItem value="coffee">Coffee Chat</SelectItem>
                  <SelectItem value="email">Email</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        );

      case 'resource':
        return (
          <div className="space-y-4">
            <div>
              <Label>Recommended Resources</Label>
              <div className="space-y-2 mt-2">
                {insight.suggestedAction.details.resources?.map((resource: any, index: number) => (
                  <Card key={index} className="p-3">
                    <div className="font-medium">{resource.title}</div>
                    <div className="text-sm text-muted-foreground">{resource.type}</div>
                    <div className="text-sm mt-1">{resource.description}</div>
                  </Card>
                ))}
              </div>
            </div>
            <div>
              <Label htmlFor="commitment">Time Commitment</Label>
              <Select
                defaultValue="15min"
                onValueChange={(value) => setCustomization({ ...customization, timeCommitment: value })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="15min">15 min/day</SelectItem>
                  <SelectItem value="30min">30 min/day</SelectItem>
                  <SelectItem value="1hour">1 hour/week</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        );

      default:
        return <div>Unknown action type</div>;
    }
  };

  const executeAction = async () => {
    if (!user) return;

    setIsExecuting(true);
    try {
      const actionData = {
        ...insight.suggestedAction.details,
        ...customization,
      };

      // Check for conflicts before execution
      await checkForConflicts(actionData);

      switch (insight.actionType) {
        case 'goal':
          // Create a new goal
          // Format date as YYYY-MM-DD (backend validation requirement)
          // Note: apiService returns data directly or throws on error
          const goalTargetDate = actionData.targetDate 
            ? new Date(actionData.targetDate).toISOString().split('T')[0]
            : new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0];
          
          await goalsService.createGoal({
            title: actionData.title,
            description: actionData.description,
            category: insight.category,
            priority: insight.impact === 'high' ? 'high' : 'medium',
            dueDate: goalTargetDate,
          });

          toast.success('Goal created successfully! Check your goals tab.');
          break;

        case 'schedule':
          // Create a recurring reminder/event using valid notification type
          // Note: apiService returns data directly or throws on error
          await notificationsService.create({
            type: 'insight',
            title: `Time for: ${actionData.activity}`,
            message: `Based on your patterns, this is a great time for ${actionData.activity}`,
            data: {
              activity: actionData.activity,
              time: actionData.time,
              duration: actionData.duration,
              frequency: actionData.frequency,
              source: 'ai_insights',
              action_type: 'scheduled_activity',
            },
          });

          toast.success('Activity scheduled! You\'ll receive reminders.');
          break;

        case 'habit':
          // Habits table doesn't exist in backend, create a goal instead
          // Note: apiService returns data directly or throws on error
          const habitDescription = actionData.trigger 
            ? `Trigger: ${actionData.trigger}` 
            : 'Build this habit consistently';
          
          await goalsService.createGoal({
            title: `Build habit: ${actionData.habit || 'New habit'}`,
            description: habitDescription,
            category: 'personal_growth',
            priority: 'medium',
          });

          toast.success('Habit goal created! Track progress in your goals.');
          break;

        case 'connect':
          // Create connection reminder using valid notification type
          // Note: apiService returns data directly or throws on error
          await notificationsService.create({
            type: 'team',
            title: `Connect with ${actionData.person || 'team member'}`,
            message: actionData.purpose || 'Connect with your team member',
            data: {
              person: actionData.person,
              method: actionData.method,
              purpose: actionData.purpose,
              action_type: 'connection_reminder',
            },
          });

          toast.success('Connection reminder set! Take action when ready.');
          break;

        case 'resource':
          // Save resource recommendations using valid notification type
          // Note: apiService returns data directly or throws on error
          await notificationsService.create({
            type: 'insight',
            title: 'AI-Recommended Resources',
            message: 'Based on your patterns, these resources can help you grow',
            data: {
              resources: actionData.resources,
              timeCommitment: actionData.timeCommitment,
              action_type: 'resource_recommendation',
            },
          });

          toast.success('Resources saved to your notifications!');
          break;
      }

      onActionComplete?.();
      onClose();
    } catch (error) {
      console.error('Error executing action:', error);
      toast.error('Failed to execute action. Please try again.');
    } finally {
      setIsExecuting(false);
    }
  };

  const getImpactColor = (impact: string) => {
    switch (impact) {
      case 'high': return 'text-red-600 bg-red-100';
      case 'medium': return 'text-yellow-600 bg-yellow-100';
      case 'low': return 'text-green-600 bg-green-100';
      default: return 'text-gray-600 bg-gray-100';
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Sparkles className="h-5 w-5 text-primary" />
            {insight.title}
          </DialogTitle>
          <DialogDescription>{insight.description}</DialogDescription>
        </DialogHeader>

        <div className="space-y-6">
          {/* Impact and Category */}
          <div className="flex gap-2">
            <Badge className={getImpactColor(insight.impact)}>
              {insight.impact} impact
            </Badge>
            <Badge variant="outline">{insight.category}</Badge>
          </div>

          {/* AI Reasoning */}
          <Card className="bg-blue-50 border-blue-200">
            <CardContent className="pt-4">
              <div className="flex items-start gap-2">
                <Brain className="h-5 w-5 text-blue-600 mt-0.5" />
                <div>
                  <p className="font-medium text-blue-900">Why this action?</p>
                  <p className="text-sm text-blue-700 mt-1">{insight.reasoning}</p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Expected Outcome */}
          <Card className="bg-green-50 border-green-200">
            <CardContent className="pt-4">
              <div className="flex items-start gap-2">
                <Target className="h-5 w-5 text-green-600 mt-0.5" />
                <div>
                  <p className="font-medium text-green-900">Expected Outcome</p>
                  <p className="text-sm text-green-700 mt-1">{insight.expectedOutcome}</p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Action Customization */}
          <div>
            <h3 className="font-medium mb-3 flex items-center gap-2">
              <CheckCircle className="h-4 w-4" />
              Customize Your Action
            </h3>
            {getActionUI()}
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={executeAction} disabled={isExecuting}>
            {isExecuting ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2" />
                Executing...
              </>
            ) : (
              'Approve & Execute'
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};